import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type {
  CrmAssignmentRule,
  CrmEmailAccount,
  CrmPipeline,
  CrmScoringRule,
  CrmWorkflow,
} from '@/types/crm';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function CrmSetupPage() {
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [scoringRules, setScoringRules] = useState<CrmScoringRule[]>([]);
  const [assignmentRules, setAssignmentRules] = useState<CrmAssignmentRule[]>([]);
  const [workflows, setWorkflows] = useState<CrmWorkflow[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<CrmEmailAccount[]>([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    Promise.all([
      crmApi.listPipelines(),
      crmApi.listScoringRules().catch(() => [] as CrmScoringRule[]),
      crmApi.listAssignmentRules().catch(() => [] as CrmAssignmentRule[]),
      crmApi.listWorkflows().catch(() => [] as CrmWorkflow[]),
      crmApi.listEmailAccounts().catch(() => [] as CrmEmailAccount[]),
    ])
      .then(([p, s, a, w, e]) => {
        setPipelines(p);
        setScoringRules(s);
        setAssignmentRules(a);
        setWorkflows(w);
        setEmailAccounts(e);
      })
      .catch((err) => showApiError(err, 'Failed to load setup'));
  };

  useEffect(() => {
    load();
  }, []);

  async function onEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await crmApi.createEmailAccount({
        provider: String(fd.get('provider') ?? 'SMTP'),
        emailAddress: String(fd.get('emailAddress') ?? ''),
        imapHost: String(fd.get('imapHost') ?? '') || undefined,
        imapPort: fd.get('imapPort') ? Number(fd.get('imapPort')) : undefined,
        imapUsername: String(fd.get('imapUsername') ?? '') || undefined,
        imapPassword: String(fd.get('imapPassword') ?? '') || undefined,
        syncEnabled: fd.get('syncEnabled') === 'on',
      });
      e.currentTarget.reset();
      load();
    } catch (err) {
      showApiError(err, 'Failed to connect email account');
    }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      const count = await crmApi.syncEmailAccounts();
      load();
      notify.success(`Synced ${count} new email activities`);
    } catch (err) {
      showApiError(err, 'Email sync failed');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="CRM Setup"
        description="Pipelines, scoring, assignment, workflows, lead sources, territories, email sync"
        breadcrumbs={[{ label: 'CRM', to: '/crm' }, { label: 'Setup' }]}
        actions={
          <Button
            onClick={() =>
              crmApi
                .ensureDefaultPipeline()
                .then(load)
                .catch((e) => showApiError(e, 'Setup failed'))
            }
          >
            Ensure default pipeline
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <SetupPanel title="Pipelines">
          <ul className="text-sm space-y-2">
            {pipelines.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.name}</span>
                {p.defaultPipeline && <span className="ml-2 text-xs text-primary-600">default</span>}
                <ul className="ml-4 text-slate-600">
                  {p.stages.map((s) => (
                    <li key={s.id}>
                      {s.name} ({s.stageType}) — {s.probabilityPercentage}%
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </SetupPanel>

        <SetupPanel title="Scoring rules">
          <ul className="text-sm space-y-1">
            {scoringRules.map((r) => (
              <li key={r.id}>
                {r.ruleName}: {r.eventType} +{r.scoreDelta}
              </li>
            ))}
            {scoringRules.length === 0 && <li className="text-slate-500">No scoring rules</li>}
          </ul>
        </SetupPanel>

        <SetupPanel title="Assignment rules">
          <ul className="text-sm space-y-1">
            {assignmentRules.map((r) => (
              <li key={r.id}>
                {r.ruleName} (priority {r.priorityOrder})
              </li>
            ))}
            {assignmentRules.length === 0 && <li className="text-slate-500">No assignment rules</li>}
          </ul>
        </SetupPanel>

        <SetupPanel title="Workflow automations">
          <ul className="text-sm space-y-1">
            {workflows.map((w) => (
              <li key={w.id}>
                {w.workflowName} → {w.triggerEvent} ({w.actions?.length ?? 0} actions)
              </li>
            ))}
            {workflows.length === 0 && <li className="text-slate-500">No workflows configured</li>}
          </ul>
        </SetupPanel>

        <SetupPanel title="Email accounts (IMAP sync)">
          <ul className="text-sm space-y-1 mb-3">
            {emailAccounts.map((a) => (
              <li key={a.id}>
                {a.emailAddress}
                {a.syncEnabled && <span className="ml-2 text-xs text-green-600">sync on</span>}
              </li>
            ))}
            {emailAccounts.length === 0 && <li className="text-slate-500">No accounts connected</li>}
          </ul>
          <Button onClick={syncNow} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button
            variant="secondary"
            className="ml-2"
            onClick={() =>
              crmApi
                .googleOAuthAuthorizeUrl()
                .then(({ url }) => {
                  window.location.href = url;
                })
                .catch((e) => showApiError(e, 'Google OAuth not configured'))
            }
          >
            Connect Gmail (XOAUTH2)
          </Button>
          <form className="mt-4 space-y-2 text-sm" onSubmit={onEmailSubmit} id="crm-email-form">
            <Input name="emailAddress" type="email" label="Email" required placeholder="Email" />
            <Input name="imapHost" label="IMAP host" placeholder="IMAP host" />
            <Input name="imapPort" type="number" label="IMAP port" placeholder="IMAP port (993)" />
            <Input name="imapUsername" label="IMAP username" placeholder="IMAP username" />
            <Input name="imapPassword" type="password" label="IMAP password" placeholder="IMAP password" />
            <label className="flex items-center gap-2">
              <input name="syncEnabled" type="checkbox" defaultChecked />
              Enable scheduled sync
            </label>
            <Button type="submit">
              Connect account
            </Button>
          </form>
        </SetupPanel>
      </div>
    </div>
  );
}

function SetupPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="panel">
      <h3 className="font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}
